import {
  getDocumentsPageUrl,
  getExpenseClaimPageUrl,
  getFilesPageUrl,
  getInvoicingPageUrl,
  getSettingsPageUrl,
  getVATPageUrl,
  NotificationType
} from '@constants'
import {
  useAIHelpContext,
  PermissionSet,
  useBusinessContext,
  useFlag,
  usePermission
} from '@context'
import {
  fetchDocument,
  fetchFile,
  fetchPurchaseInvoice,
  INotification2,
  updateNotification
} from '@query'
import dayjs from 'dayjs'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FaCircle } from 'react-icons/fa'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import { NotificationIcon } from './NotificationIcon'
import { useNotificationColor } from './useNotificationColor'

interface NotificationProps {
  notification: INotification2
}

interface NotificationContent {
  title: string
  description: string
}

const useRelatedModelData = (notification: INotification2) => {
  const canViewFiles = usePermission(PermissionSet.FILES_VIEW)
  const canViewInvoicing = usePermission(PermissionSet.INVOICING_VIEW)
  const canViewBookkeeping = usePermission(PermissionSet.BOOKKEEPING_VIEW)

  const { data: file } = useQuery({
    queryKey: [notification?.business.business_id, 'file', notification.related_object_id],
    queryFn: () =>
      fetchFile({
        slug: notification?.business.business_id,
        fileId: notification.related_object_id
      }),
    ...{
      enabled: canViewFiles && notification.type === NotificationType.FILE_UPLOADED_VIA_EMAIL
    }
  })

  const { data: purchaseInvoice } = useQuery({
    queryKey: [
      notification?.business.business_id,
      'purchase_invoice',
      notification.related_object_id
    ],
    queryFn: () =>
      fetchPurchaseInvoice({
        slug: notification?.business.business_id,
        purchaseInvoiceId: notification.related_object_id
      }),
    ...{
      enabled:
        canViewInvoicing &&
        (notification.type === NotificationType.INVOICE_RECEIVED ||
          notification.type === NotificationType.PURCHASE_INVOICE_EMAIL_IMPORT_SUCCESS ||
          notification.type === NotificationType.PURCHASE_INVOICE_DUE)
    }
  })

  const { data: document } = useQuery({
    queryKey: [notification?.business.business_id, 'document', notification.related_object_id],
    queryFn: () =>
      fetchDocument({
        slug: notification?.business.business_id,
        documentId: notification.related_object_id
      }),
    ...{
      enabled:
        canViewBookkeeping && notification.type === NotificationType.USER_MENTIONED_IN_COMMENT
    }
  })

  return { file, purchaseInvoice, document }
}

const useNotificationContent = (notification: INotification2, t: any): NotificationContent => {
  const related = useRelatedModelData(notification)

  return useMemo(() => {
    if (notification.type === NotificationType.CUSTOM)
      return {
        title: notification.custom_message_title,
        description: notification.custom_message_content
      }

    if (notification.type === NotificationType.AI_CHAT_MESSAGE)
      return {
        title: notification.custom_message_title,
        description: notification.custom_message_content
      }

    if (notification.custom_message_title || notification.custom_message_content) {
      return {
        title: notification.custom_message_title ?? t(`notification.${notification.type}.title`),
        description:
          notification.custom_message_content ?? t(`notification.${notification.type}.description`)
      }
    }

    if (notification.type === NotificationType.FILE_UPLOADED_VIA_EMAIL) {
      return {
        title: t(`notification.${notification.type}.title`),
        description: t(`notification.${notification.type}.description`, {
          fileName: related.file?.name
        })
      }
    }

    if (notification.type === NotificationType.INVOICE_RECEIVED) {
      return {
        title: t(`notification.${notification.type}.title`),
        description: t(`notification.${notification.type}.description`, {
          senderName: related.purchaseInvoice?.sender_name
        })
      }
    }

    if (notification.type === NotificationType.USER_MENTIONED_IN_COMMENT) {
      return {
        title: t('notification.USER_MENTIONED_IN_COMMENT.title'),
        description: t('notification.USER_MENTIONED_IN_COMMENT.description', {
          documentNumber: related.document?.number
        })
      }
    }

    if (notification.type === NotificationType.PURCHASE_INVOICE_EMAIL_IMPORT_SUCCESS) {
      return {
        title: t('notification.PURCHASE_INVOICE_EMAIL_IMPORT_SUCCESS.title'),
        description: t('notification.PURCHASE_INVOICE_EMAIL_IMPORT_SUCCESS.description', {
          sender: related.purchaseInvoice?.sender_name
        })
      }
    }

    return {
      title: t(`notification.${notification.type}.title`),
      description: t(`notification.${notification.type}.description`)
    }
  }, [notification, related.file?.id, related.purchaseInvoice?.id, related.document?.id, t])
}

const useNotificationLink = (notification: INotification2): string | undefined => {
  const related = useRelatedModelData(notification)

  return useMemo(() => {
    if (
      notification.type === NotificationType.CUSTOM ||
      notification.type === NotificationType.AI_CHAT_MESSAGE
    ) {
      return notification.custom_message_link
    }

    if (notification.type === NotificationType.VAT_PERIOD_REPORTING_DUE_1) {
      return getVATPageUrl(notification?.business.business_id, {
        id: notification.related_object_id
      })
    }

    if (notification.type === NotificationType.VAT_PERIOD_REPORTING_DUE_2) {
      return getVATPageUrl(notification?.business.business_id, {
        id: notification.related_object_id
      })
    }

    if (notification.type === NotificationType.VAT_PERIOD_REPORTING_DUE_TOMORROW) {
      return getVATPageUrl(notification?.business.business_id, {
        id: notification.related_object_id
      })
    }

    if (notification.type === NotificationType.FILE_UPLOADED_VIA_EMAIL) {
      return getFilesPageUrl(notification?.business.business_id, {
        idType: 'file',
        id: related.file?.id,
        folder: related.file?.folder_id
      })
    }

    if (notification.type === NotificationType.BANK_INTEGRATION_SUSPENDED) {
      return getSettingsPageUrl(notification?.business.business_id, {
        page: 'integrations'
      })
    }

    if (notification.type === NotificationType.HOLVI_INTEGRATION_SUSPENDED) {
      const businessId = notification.business?.business_id
      return businessId ? getSettingsPageUrl(businessId, { page: 'holvi' }) : undefined
    }

    if (notification.type === NotificationType.BANK_INTEGRATION_GOING_TO_EXPIRE) {
      return getSettingsPageUrl(notification?.business.business_id, {
        page: 'integrations'
      })
    }

    if (notification.type === NotificationType.INVOICE_RECEIVED) {
      return getInvoicingPageUrl(notification?.business.business_id, {
        type: 'purchase',
        id: notification.related_object_id
      })
    }

    if (notification.type === NotificationType.USER_MENTIONED_IN_COMMENT) {
      return getDocumentsPageUrl(notification?.business.business_id, {
        id: notification.related_object_id,
        tab: 'audit_trail'
      })
    }

    if (notification.type === NotificationType.PURCHASE_INVOICE_EMAIL_IMPORT_SUCCESS) {
      return getInvoicingPageUrl(notification?.business.business_id, {
        type: 'purchase',
        id: notification.related_object_id
      })
    }

    if (notification.type === NotificationType.PURCHASE_INVOICE_DUE) {
      return getInvoicingPageUrl(notification?.business.business_id, {
        type: 'purchase',
        id: notification.related_object_id
      })
    }

    if (notification.type === NotificationType.ACCOUNTING_SUGGESTIONS_AVAILABLE) {
      return getDocumentsPageUrl(notification?.business.business_id, {
        id: notification.related_object_id ?? undefined,
        tab: 'suggestions'
      })
    }

    if (
      notification.type === NotificationType.EXPENSE_CLAIM_AWAITING_APPROVAL ||
      notification.type === NotificationType.EXPENSE_CLAIM_APPROVED ||
      notification.type === NotificationType.EXPENSE_CLAIM_REJECTED ||
      notification.type === NotificationType.EXPENSE_CLAIM_APPROVAL_CANCELLED ||
      notification.type === NotificationType.EXPENSE_CLAIM_APPROVAL_REMINDER
    ) {
      const businessId = notification.business?.business_id
      if (!businessId || notification.related_object_id == null) return undefined
      return getExpenseClaimPageUrl(businessId, notification.related_object_id)
    }

    return undefined
  }, [
    related.file?.id,
    related.purchaseInvoice?.id,
    notification.type,
    notification.related_object_id,
    notification.custom_message_link,
    notification.business?.business_id
  ])
}

const isAbsoluteUrl = (path: string): boolean => {
  const pat = /^https?:\/\//i
  return pat.test(path)
}

export const Notification: React.FC<NotificationProps> = ({ notification }) => {
  const history = useHistory()
  const queryClient = useQueryClient()
  const [t] = useTranslation()
  const { title, description } = useNotificationContent(notification, t)
  const to = useNotificationLink(notification)
  const color = useNotificationColor(notification)
  const { slug } = useBusinessContext()
  const {
    setAIHelpOpen,
    setAllBusinesses,
    setPendingConversationId,
    setWidgetBusinessSlugOverride
  } = useAIHelpContext()
  const useCustomAiWidget = useFlag('custom_ai_widget')
  const showBusinessesBadge = !slug

  const { mutate: markAsSeen } = useMutation({
    mutationFn: () => updateNotification({ notificationId: notification.id }, { seen: true }),
    ...{
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['notifications'] })
      }
    }
  })

  const handleOnClick = useCallback(
    e => {
      if (!notification.seen) markAsSeen()

      if (
        useCustomAiWidget.enabled &&
        notification.type === NotificationType.AI_CHAT_MESSAGE &&
        notification.conversation_id
      ) {
        const targetBusinessSlug = notification.business?.slug || slug || null
        setPendingConversationId(notification.conversation_id)
        setWidgetBusinessSlugOverride(targetBusinessSlug)
        setAllBusinesses(false)
        setAIHelpOpen(true)
        e.preventDefault()
        e.stopPropagation()
        return
      }

      // CASE: Link is not external
      // > Push the new path to the react router dom history
      if (to && !isAbsoluteUrl(to)) {
        history.push(to)

        // Prevent original link opening
        e.preventDefault()
        e.stopPropagation()
      }
    },
    [
      notification.seen,
      notification.type,
      notification.conversation_id,
      to,
      history,
      markAsSeen,
      setAIHelpOpen,
      setAllBusinesses,
      setPendingConversationId,
      setWidgetBusinessSlugOverride,
      slug,
      useCustomAiWidget.enabled
    ]
  )

  return (
    <StyledNotification href={to || '#'} target="_blank" onClick={handleOnClick}>
      {showBusinessesBadge && (
        <StyledBusinessInfo>{notification?.business.name}</StyledBusinessInfo>
      )}
      <StyledNotificationTitle>
        {!notification.seen && <SeenIcon color={color} />} <span>{title}</span>
        <StyledTimestamp>{dayjs(notification.timestamp).fromNow()}</StyledTimestamp>
      </StyledNotificationTitle>
      <StyledNotificationContent>
        <StyledNotificationDescription>{description}</StyledNotificationDescription>
        <StyledNotificationIcon $intentColor={color}>
          <NotificationIcon notification={notification} />
        </StyledNotificationIcon>
      </StyledNotificationContent>
    </StyledNotification>
  )
}

const StyledNotification = styled.a`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.md}rem;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  text-decoration: none !important;

  &:hover {
    background: ${({ theme }) => theme.colors.defaultHover};
  }
`

const StyledNotificationIcon = styled.div<{ $intentColor?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.2rem;
  height: 2.2rem;
  border-radius: 50%;
  background: ${({ $intentColor }) => ($intentColor ? `${$intentColor}15` : 'rgba(0, 0, 0, 0.04)')};
  flex-shrink: 0;

  div {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  svg {
    font-size: 1.1rem !important;
    color: ${({ $intentColor }) => $intentColor || 'inherit'} !important;
    fill: ${({ $intentColor }) => $intentColor || 'inherit'} !important;
  }
`

const StyledNotificationContent = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

const StyledNotificationTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  margin: 0;
  min-width: 0;

  & > span {
    flex: 1;
    font-size: ${({ theme }) => theme.fontSize.md}rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.neutralBlack};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }
`

const StyledNotificationDescription = styled.p`
  flex: 1;
  font-size: 0.85rem;
  margin: 0;
  color: ${({ theme }) => theme.colors.metalGray};
  line-height: 1.45;
`

const SeenIcon = styled(FaCircle)`
  font-size: 0.5rem;
  flex-shrink: 0;

  @keyframes pulseGlow {
    0% {
      transform: scale(0.9);
      opacity: 0.65;
    }
    50% {
      transform: scale(1.2);
      opacity: 1;
    }
    100% {
      transform: scale(0.9);
      opacity: 0.65;
    }
  }

  animation: pulseGlow 2s infinite ease-in-out;
`

const StyledTimestamp = styled.div`
  font-weight: 500;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.iconGray};
  flex-shrink: 0;
`

const StyledBusinessInfo = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xxs}rem;
  background: ${({ theme }) => theme.colors.neutralGray};
  color: ${({ theme }) => theme.colors.metalGray};
  font-weight: 600;
  border-radius: 100px;
  padding: 0.15rem 0.45rem;
  margin-bottom: 0.1rem;
  width: fit-content;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`
